import React, { useEffect, useState } from 'react'
import { Button, Card, Chip, Heading, Icon, LoadingFlex } from '@arnsbogroup/react-voicearchive'
import { FormattedMessage, useIntl } from 'react-intl'
import MemoryListBlock from 'components/MemoryListBlock'
import messages from './messages'

const FALLBACK_LOGO = 'https://voicearchive-branding.s3.eu-central-1.amazonaws.com/small-curve-white.svg'

const PublicRecordingRequestPage = ({ match }) => {
  const { formatMessage } = useIntl()
  const hash = match.params.hash
  const [request, setRequest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showUpload, setShowUpload] = useState(false)

  const loadRequest = () => {
    fetch(`/rest/v2/memoryrequest/${hash}`)
      .then((r) => r.json())
      .then(({ success, data, errors }) => {
        if (!success) {
          setError((errors || []).join(', '))
          return
        }
        setRequest(data)
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadRequest() }, [hash])

  const renderWithBranding = (content) => {
    const hasBranding = request?.brand != null
    const logo = request?.brand?.logo || request?.brand?.logo_dark || request?.brand?.icon || request?.brand?.banner || FALLBACK_LOGO
    const brandName = request?.brand?.displayName || request?.brand?.name || 'VoiceArchive'

    return (
      <div className="min-h-screen bg-gray-50 w-full">
        {hasBranding && (
          <div className="py-4 px-6 mb-8" style={{ backgroundColor: request?.brand?.theme?.primary?.default || '#326295' }}>
            <div className="max-w-2xl mx-auto flex items-center justify-center">
              <img
                src={logo}
                alt={brandName}
                className="h-10 object-contain"
                onError={(e) => { e.target.src = FALLBACK_LOGO }}
              />
            </div>
          </div>
        )}
        <div className="px-4 pb-12" style={hasBranding ? {} : { paddingTop: '2.5rem' }}>
          {content}
        </div>
      </div>
    )
  }

  if (loading) return <LoadingFlex />

  if (error || !request) {
    return renderWithBranding(
      <div className="max-w-2xl mx-auto text-center py-16">
        <p className="text-gray-500">{error || formatMessage(messages.notFound)}</p>
      </div>
    )
  }

  const pendingCount = (request.pendingMemories || []).length
  const completedCount = (request.completedMemories || []).length
  const totalCount = pendingCount + completedCount
  const allDone = pendingCount === 0 && completedCount > 0

  const pendingMemories = (request.pendingMemories || []).map((m) => ({
    ...m,
    allow_record: true,
    recording_request_hash: hash,
  }))

  const completedMemories = request.completedMemories || []

  return renderWithBranding(
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="space-y-3 text-center">
        <Heading level={1} className="text-2xl font-bold">
          {formatMessage(messages.pageTitle)}
        </Heading>
        {request.body && (
          <p className="text-gray-600 text-sm leading-relaxed">{request.body}</p>
        )}
        {totalCount > 0 && (
          <Chip size="sm" state={allDone ? 'positive' : 'primary'} className="mx-auto">
            {formatMessage(messages.progressChip, { completed: completedCount, total: totalCount })}
          </Chip>
        )}
      </div>

      <Card className="p-4 text-sm text-gray-700 space-y-2 leading-relaxed">
        <p><FormattedMessage {...messages.introHi} /></p>
        <p><FormattedMessage {...messages.introPrompt} /></p>
        <p>
          <FormattedMessage
            {...messages.introStart}
            values={{ recordButton: <strong className="mx-1">Record</strong> }}
          />
        </p>
        <p>
          <FormattedMessage
            {...messages.introInstructions}
            values={{
              stopIcon: <Icon icon={['fas', 'stop']} className="mx-1 text-gray-600" />,
              playIcon: <Icon icon={['fas', 'play']} className="mx-1 text-gray-600" />,
              reRecordIcon: <Icon icon={['fas', 'microphone']} className="mx-1 text-gray-600" />,
            }}
          />
        </p>
        <p><FormattedMessage {...messages.introNotification} /></p>
      </Card>

      {allDone && (
        <Card className="p-4 bg-green-50 border-green-200 text-center">
          <p className="text-green-700 font-semibold">
            {formatMessage(messages.allDone)}
          </p>
        </Card>
      )}

      {pendingCount > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {formatMessage(messages.pendingHeading)}
            </h2>
            <Button
              size="xs"
              variant='outline'
              onClick={() => setShowUpload(prev => !prev)}
            >
              {showUpload ? formatMessage(messages.hideUpload) : formatMessage(messages.cantRecord)}
            </Button>
          </div>

          {showUpload && (
            <Card className="p-3 bg-amber-50 border-amber-200">
              <p className="text-xs text-amber-800">
                {formatMessage(messages.uploadDescription)}
              </p>
            </Card>
          )}

          <MemoryListBlock
            title={null}
            memories={pendingMemories}
            onRecord={loadRequest}
            showUpload={showUpload}
          />
        </section>
      )}

      {completedCount > 0 && (
        <section className="space-y-3">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            {formatMessage(messages.completedHeading)}
          </h2>
          <MemoryListBlock
            title={null}
            memories={completedMemories}
          />
        </section>
      )}
    </div>
  )
}

export default PublicRecordingRequestPage
